import verificationClock from '@/assets/kyc/verification-clock.png';
import verificationEllipse from '@/assets/kyc/verification-ellipse.svg';
import verificationShield from '@/assets/kyc/verification-shield.png';
import { Medallion } from '@/components/features/Notifications/popups/Medallion';
import { PopupCta, PopupLinkCta } from '@/components/features/Notifications/popups/PopupCta';
import { PopupShell } from '@/components/features/Notifications/popups/PopupShell';

/** Default body copy — the identity-verification wording from the Figma design. */
const DEFAULT_DESCRIPTION = "Your identity verification is being reviewed. We'll notify you when it's complete.";

export interface KycInProgressPanelProps {
  /** "Got It" tap (and backdrop / Escape) — dismisses the gate. Never launches the SDK. */
  onClose: () => void;
  /** "Contact Support" tap. */
  onContactSupport: () => void;
  /** Body copy. Defaults to the identity wording; the action gate passes its own ("This check…"). */
  description?: string;
  /**
   * Prefix for the panel's test ids — `${testIdBase}-panel` / `-gotit` / `-support`.
   * Defaults to `kyc-onhold` so the ON_HOLD usage keeps its existing ids; the
   * transient processing states pass `kyc-processing` / `kyc-action-processing`.
   */
  testIdBase?: string;
}

/**
 * "Verification in Progress" — the shared branded pop-up for every KYC state
 * that means "we're reviewing you, sit tight". Backs three call sites:
 *  - identity ON_HOLD (manual review),
 *  - the identity gate's transient `processing` state (client-side GREEN, backend
 *    webhook not yet landed),
 *  - the action gate's equivalent `processing` state.
 * Never mints a token or launches the Sumsub SDK, and never hangs re-minting —
 * the only actions are "Got It" (dismiss) and "Contact Support". The hero is the
 * shield-clock medallion: a themeable peach circle (SVG) + 3D shield (PNG) via
 * `Medallion`, with the clock hands composited on top (Figma exports the hands as
 * a separate overlay, positioned per its 190px artboard coords).
 */
export function KycInProgressPanel({
  onClose,
  onContactSupport,
  description = DEFAULT_DESCRIPTION,
  testIdBase = 'kyc-onhold',
}: KycInProgressPanelProps) {
  return (
    <PopupShell open onClose={onClose} testId={`${testIdBase}-panel`}>
      <div className="relative flex size-[190px] shrink-0 items-center justify-center">
        <Medallion
          circleSrc={verificationEllipse}
          illustrationSrc={verificationShield}
          tone="warning"
          size={190}
          illustrationSize={149}
        />
        <img
          src={verificationClock}
          alt=""
          aria-hidden
          className="pointer-events-none absolute left-[87px] top-[64px] size-9 object-contain"
        />
      </div>
      <div className="flex flex-col items-center gap-3">
        <h2 className="text-3xl font-semibold tracking-[-0.6px] text-ink">Verification in Progress</h2>
        <p className="max-w-[394px] text-xl leading-[30px] text-ink">{description}</p>
      </div>
      <div className="flex flex-col items-center gap-4">
        <PopupCta label="Got It" width={234} onClick={onClose} testId={`${testIdBase}-gotit`} />
        <PopupLinkCta label="Contact Support" onClick={onContactSupport} testId={`${testIdBase}-support`} />
      </div>
    </PopupShell>
  );
}
